export interface CachedImage {
  url: string;
  blob: Blob;
  size: number;
  lastAccessed: number;
  accessCount: number;
  priority: 'low' | 'medium' | 'high' | 'critical';
  expiresAt?: number;
  metadata: {
    width: number;
    height: number;
    format: string;
    originalSize: number;
    compressionRatio: number;
  };
}

export interface CacheStats {
  totalImages: number;
  totalSize: number;
  hitRate: number;
  missRate: number;
  evictionCount: number;
  averageAccessTime: number;
  memoryUsage: number;
}

export interface CacheConfig {
  maxSize: number; // MB
  maxImages: number;
  ttl: number; // segundos
  priorityWeights: {
    low: number;
    medium: number;
    high: number;
    critical: number;
  };
  evictionStrategy: 'lru' | 'lfu' | 'fifo' | 'priority';
}

export class IntelligentImageCache {
  private static readonly DEFAULT_CONFIG: CacheConfig = {
    maxSize: 100, // 100MB
    maxImages: 1000,
    ttl: 24 * 60 * 60, // 24 horas
    priorityWeights: {
      low: 1,
      medium: 2,
      high: 3,
      critical: 5
    },
    evictionStrategy: 'lru'
  };

  private static config: CacheConfig = { ...this.DEFAULT_CONFIG };
  private static cache = new Map<string, CachedImage>();
  private static accessLog = new Map<string, number[]>();
  private static stats = {
    hits: 0,
    misses: 0,
    evictions: 0,
    totalRequests: 0
  };

  /**
   * Configura o cache
   */
  static configure(config: Partial<CacheConfig>): void {
    this.config = { ...this.DEFAULT_CONFIG, ...config };
    if (import.meta.env.DEV) console.log('🧠 Cache inteligente configurado:', this.config);
  }

  /**
   * Adiciona imagem ao cache
   */
  static async addToCache(
    url: string,
    blob: Blob,
    priority: CachedImage['priority'] = 'medium',
    metadata?: Partial<CachedImage['metadata']>
  ): Promise<boolean> {
    try {
      // Verificar se já existe
      if (this.cache.has(url)) {
        this.updateAccess(url);
        return true;
      }

      // Verificar limites antes de adicionar
      await this.ensureSpace(blob.size);

      // Criar entrada do cache
      const cachedImage: CachedImage = {
        url,
        blob,
        size: blob.size,
        lastAccessed: Date.now(),
        accessCount: 1,
        priority,
        expiresAt: Date.now() + (this.config.ttl * 1000),
        metadata: {
          width: 0,
          height: 0,
          format: 'unknown',
          originalSize: blob.size,
          compressionRatio: 0,
          ...metadata
        }
      };

      // Extrair metadados da imagem se não fornecidos
      if (!metadata?.width || !metadata?.height) {
        try {
          const img = await this.loadImage(blob);
          cachedImage.metadata.width = img.width;
          cachedImage.metadata.height = img.height;
          cachedImage.metadata.format = blob.type.split('/')[1] || 'unknown';
        } catch (error) {
          console.warn('Erro ao extrair metadados da imagem:', error);
        }
      }

      // Adicionar ao cache
      this.cache.set(url, cachedImage);
      this.accessLog.set(url, [Date.now()]);

      console.log(`✅ Imagem adicionada ao cache: ${url} (${this.formatBytes(blob.size)})`);
      return true;

    } catch (error) {
      console.error('Erro ao adicionar ao cache:', error);
      return false;
    }
  }

  /**
   * Obtém imagem do cache
   */
  static async getFromCache(url: string): Promise<CachedImage | null> {
    try {
      const cached = this.cache.get(url);
      
      if (!cached) {
        this.stats.misses++;
        this.stats.totalRequests++;
        return null;
      }

      // Verificar expiração
      if (cached.expiresAt && Date.now() > cached.expiresAt) {
        console.log(`⏰ Imagem expirada removida: ${url}`);
        this.cache.delete(url);
        this.accessLog.delete(url);
        this.stats.misses++;
        this.stats.totalRequests++;
        return null;
      }

      // Atualizar estatísticas de acesso
      this.updateAccess(url);
      this.stats.hits++;
      this.stats.totalRequests++;

      return cached;

    } catch (error) {
      console.error('Erro ao obter do cache:', error);
      this.stats.misses++;
      this.stats.totalRequests++;
      return null;
    }
  }

  /**
   * Verifica se imagem está no cache
   */
  static hasInCache(url: string): boolean {
    const cached = this.cache.get(url);
    if (!cached) return false;

    // Verificar expiração
    if (cached.expiresAt && Date.now() > cached.expiresAt) {
      this.cache.delete(url);
      this.accessLog.delete(url);
      return false;
    }

    return true;
  }

  /**
   * Remove imagem do cache
   */
  static removeFromCache(url: string): boolean {
    const cached = this.cache.get(url);
    if (!cached) return false;

    this.cache.delete(url);
    this.accessLog.delete(url);
    console.log(`🗑️ Imagem removida do cache: ${url}`);
    return true;
  }

  /**
   * Atualiza estatísticas de acesso
   */
  private static updateAccess(url: string): void {
    const cached = this.cache.get(url);
    if (!cached) return;

    cached.lastAccessed = Date.now();
    cached.accessCount++;

    // Atualizar log de acesso
    const accessTimes = this.accessLog.get(url) || [];
    accessTimes.push(Date.now());
    
    // Manter apenas os últimos 10 acessos
    if (accessTimes.length > 10) {
      accessTimes.splice(0, accessTimes.length - 10);
    }
    
    this.accessLog.set(url, accessTimes);
  }

  /**
   * Garante espaço disponível no cache
   */
  private static async ensureSpace(requiredSize: number): Promise<void> {
    const currentSize = this.getCurrentSize();
    const maxSizeBytes = this.config.maxSize * 1024 * 1024;

    // Se ainda há espaço, não precisa fazer nada
    if (currentSize + requiredSize <= maxSizeBytes && this.cache.size < this.config.maxImages) {
      return;
    }

    console.log(`🧠 Cache cheio, iniciando eviction... (${this.formatBytes(currentSize)}/${this.formatBytes(maxSizeBytes)})`);

    // Calcular quanto espaço precisa ser liberado
    const spaceToFree = Math.max(requiredSize, (currentSize + requiredSize - maxSizeBytes) * 0.2); // Liberar 20% extra

    // Executar eviction
    await this.evictImages(spaceToFree);
  }

  /**
   * Remove imagens do cache baseado na estratégia
   */
  private static async evictImages(spaceToFree: number): Promise<void> {
    const images = Array.from(this.cache.values());
    let freedSpace = 0;
    const evictedUrls: string[] = [];

    switch (this.config.evictionStrategy) {
      case 'lru':
        // Least Recently Used
        images.sort((a, b) => a.lastAccessed - b.lastAccessed);
        break;
      
      case 'lfu':
        // Least Frequently Used
        images.sort((a, b) => a.accessCount - b.accessCount);
        break;
      
      case 'fifo':
        // First In, First Out (baseado na ordem de inserção)
        // Como não temos timestamp de inserção, usamos lastAccessed
        images.sort((a, b) => a.lastAccessed - b.lastAccessed);
        break;
      
      case 'priority':
        // Por prioridade (menor peso primeiro)
        images.sort((a, b) => this.config.priorityWeights[a.priority] - this.config.priorityWeights[b.priority]);
        break;
    }

    // Remover imagens até liberar espaço suficiente
    for (const image of images) {
      if (freedSpace >= spaceToFree) break;

      evictedUrls.push(image.url);
      freedSpace += image.size;
      this.cache.delete(image.url);
      this.accessLog.delete(image.url);
    }

    this.stats.evictions += evictedUrls.length;
    console.log(`🧠 Eviction concluído: ${evictedUrls.length} imagens removidas, ${this.formatBytes(freedSpace)} liberados`);
  }

  /**
   * Pré-carrega imagem no cache
   */
  static async preloadImage(url: string, priority: CachedImage['priority'] = 'low'): Promise<boolean> {
    try {
      console.log(`🔄 Pré-carregando imagem: ${url}`);
      
      const response = await fetch(url);
      if (!response.ok) return false;

      const blob = await response.blob();
      return await this.addToCache(url, blob, priority);

    } catch (error) {
      console.warn('Erro ao pré-carregar imagem:', error);
      return false;
    }
  }

  /**
   * Pré-carrega múltiplas imagens
   */
  static async preloadImages(urls: string[], priority: CachedImage['priority'] = 'low'): Promise<{
    success: number;
    failed: number;
    total: number;
  }> {
    let success = 0;
    let failed = 0;

    const promises = urls.map(async (url) => {
      try {
        const result = await this.preloadImage(url, priority);
        if (result) success++;
        else failed++;
      } catch (error) {
        failed++;
      }
    });

    await Promise.allSettled(promises);

    return { success, failed, total: urls.length };
  }

  /**
   * Limpa cache expirado
   */
  static cleanExpiredCache(): number {
    const now = Date.now();
    let cleaned = 0;

    for (const [url, cached] of this.cache.entries()) {
      if (cached.expiresAt && now > cached.expiresAt) {
        this.cache.delete(url);
        this.accessLog.delete(url);
        cleaned++;
      }
    }

    if (cleaned > 0) {
      console.log(`🧹 Cache limpo: ${cleaned} imagens expiradas removidas`);
    }

    return cleaned;
  }

  /**
   * Obtém estatísticas do cache
   */
  static getStats(): CacheStats {
    const totalImages = this.cache.size;
    const totalSize = this.getCurrentSize();
    const hitRate = this.stats.totalRequests > 0 ? (this.stats.hits / this.stats.totalRequests) * 100 : 0;
    const missRate = 100 - hitRate;

    // Calcular tempo médio de acesso
    let totalAccessTime = 0;
    let accessCount = 0;
    
    for (const accessTimes of this.accessLog.values()) {
      if (accessTimes.length > 1) {
        for (let i = 1; i < accessTimes.length; i++) {
          totalAccessTime += accessTimes[i] - accessTimes[i - 1];
          accessCount++;
        }
      }
    }

    const averageAccessTime = accessCount > 0 ? totalAccessTime / accessCount : 0;

    return {
      totalImages,
      totalSize,
      hitRate,
      missRate,
      evictionCount: this.stats.evictions,
      averageAccessTime,
      memoryUsage: this.getCurrentSize()
    };
  }

  /**
   * Obtém tamanho atual do cache
   */
  private static getCurrentSize(): number {
    return Array.from(this.cache.values()).reduce((total, image) => total + image.size, 0);
  }

  /**
   * Carrega imagem do blob
   */
  private static loadImage(blob: Blob): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(blob);

      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve(img);
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Falha ao carregar imagem'));
      };

      img.src = url;
    });
  }

  /**
   * Formata bytes para leitura humana
   */
  private static formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Limpa todo o cache
   */
  static clearCache(): void {
    this.cache.clear();
    this.accessLog.clear();
    this.stats = { hits: 0, misses: 0, evictions: 0, totalRequests: 0 };
    console.log('🧠 Cache completamente limpo');
  }

  /**
   * Obtém lista de URLs em cache
   */
  static getCachedUrls(): string[] {
    return Array.from(this.cache.keys());
  }

  /**
   * Obtém informações de uma imagem em cache
   */
  static getCacheInfo(url: string): CachedImage | null {
    return this.cache.get(url) || null;
  }

  /**
   * Configura TTL personalizado para uma imagem
   */
  static setImageTTL(url: string, ttlSeconds: number): boolean {
    const cached = this.cache.get(url);
    if (!cached) return false;

    cached.expiresAt = Date.now() + (ttlSeconds * 1000);
    return true;
  }

  /**
   * Atualiza prioridade de uma imagem
   */
  static updateImagePriority(url: string, priority: CachedImage['priority']): boolean {
    const cached = this.cache.get(url);
    if (!cached) return false;

    cached.priority = priority;
    return true;
  }
}
